import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, ArrowDownWideNarrow, ArrowDownNarrowWide, ArrowUpDown } from 'lucide-react';

const PreviousTimeslips = ({
  timeslips,
  searchTerm, setSearchTerm,
  slipSort, setSlipSort,
  empRateMap,
  onEditSlip,
  isAdmin,
  onDeleteSlip,
  windowDays,
  setWindowDays,
  slipsLoading,
}) => {
  const navigate = useNavigate();

  // Normalize vehicle/fuel expense values to "$X.XX" regardless of how they were stored.
  const formatMoney = (val) => {
    const num = parseFloat(String(val ?? '0').replace(/[$,]/g, ''));
    return `$${(isNaN(num) ? 0 : num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Convert any stored date string ("YYYY-MM-DD", "M/D/YYYY", ISO) to a numeric
  // timestamp so the date column sorts correctly across mixed formats.
  const dateToTimestamp = (val) => {
    if (!val) return 0;
    const s = String(val).trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)).getTime();
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (m) {
      const year = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
      return new Date(year, parseInt(m[1], 10) - 1, parseInt(m[2], 10)).getTime();
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  // Display dates uniformly as M/D/YYYY no matter how they were stored.
  const formatDate = (val) => {
    const t = dateToTimestamp(val);
    if (!t) return val || '';
    const d = new Date(t);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  let filtered = timeslips.filter((slip) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return [slip.employee, slip.date, slip.customer, slip.project, slip.project_address, slip.project_number, slip.job_type, String(slip.hours)]
      .some(f => (f || '').toLowerCase().includes(term));
  });

  if (slipSort.column) {
    filtered = [...filtered].sort((a, b) => {
      const dir = slipSort.direction === 'asc' ? 1 : -1;
      if (slipSort.column === 'hours') {
        return ((parseFloat(a.hours) || 0) - (parseFloat(b.hours) || 0)) * dir;
      }
      if (slipSort.column === 'date') {
        return (dateToTimestamp(a.date) - dateToTimestamp(b.date)) * dir;
      }
      const aVal = (a[slipSort.column] || '').toLowerCase();
      const bVal = (b[slipSort.column] || '').toLowerCase();
      return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
    });
  }

  return (
    <div className="bg-white rounded shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl text-[#5B8DB8] font-light">Previous Timeslips</h3>
        <div className="flex items-center gap-3">
          {setWindowDays && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={windowDays === null ? 'all' : String(windowDays)}
                onChange={(e) => setWindowDays(e.target.value === 'all' ? null : Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                data-testid="timeslips-window-select"
                disabled={slipsLoading}
              >
                <option value="7">Last 1 week</option>
                <option value="14">Last 2 weeks</option>
                <option value="21">Last 3 weeks</option>
                <option value="30">Last 1 month</option>
                <option value="60">Last 2 months</option>
                <option value="90">Last 3 months</option>
                <option value="180">Last 6 months</option>
                <option value="365">Last 1 year</option>
                <option value="all">All time</option>
              </select>
              {slipsLoading && <span className="text-xs text-gray-400" data-testid="timeslips-loading">Loading…</span>}
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Search:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1 text-sm w-48"
            />
          </div>
        </div>
      </div>

      {setWindowDays && windowDays !== null && !(searchTerm && searchTerm.trim().length >= 2) && (
        <div className="-mt-3 mb-4 text-xs text-gray-500" data-testid="timeslips-window-hint">
          Older entries are hidden by the “Show” filter above. Pick “All time”, or type a name in Search to find labor from any date.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b-2">
              <th className="text-left p-2 text-gray-600 font-normal">Project Number</th>
              <th className="text-left p-2 text-gray-600 font-normal">Employee</th>
              <th className="text-left p-2 text-gray-600 font-normal">
                <button onClick={() => setSlipSort(s => s.column === 'date' ? { column: 'date', direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: 'date', direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800" data-testid="sort-date">
                  {slipSort.column === 'date'
                    ? (slipSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-3 h-3 text-red-500" /> : <ArrowDownWideNarrow className="w-3 h-3 text-red-500" />)
                    : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  Date
                </button>
              </th>
              <th className="text-left p-2 text-gray-600 font-normal">
                <button onClick={() => setSlipSort(s => s.column === 'hours' ? { column: 'hours', direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: 'hours', direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800" data-testid="sort-hours">
                  {slipSort.column === 'hours'
                    ? (slipSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-3 h-3 text-red-500" /> : <ArrowDownWideNarrow className="w-3 h-3 text-red-500" />)
                    : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  Hours
                </button>
              </th>
              <th className="text-left p-2 text-gray-600 font-normal">Rate</th>
              <th className="text-left p-2 text-gray-600 font-normal">Vehicle Expenses</th>
              <th className="text-left p-2 text-gray-600 font-normal">Fuel Expense ($)</th>
              <th className="text-left p-2 text-gray-600 font-normal">Project</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((slip, index) => (
              <tr key={index} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`timeslip-row-${index}`}>
                <td className="p-2">
                  {slip.project_number ? (
                    <a
                      href={`/projects/${slip.project_number}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/projects/${slip.project_number}`); }}
                      className="text-[#5B8DB8] hover:underline cursor-pointer"
                      data-testid={`project-link-${index}`}
                    >{slip.project_number}</a>
                  ) : ''}
                </td>
                <td className="p-2">
                  <div className="text-gray-700 font-medium">{slip.employee}</div>
                  {slip.job_type && <div className="text-gray-400 text-[10px]">{slip.job_type}</div>}
                </td>
                <td className="p-2 text-gray-700">{formatDate(slip.date)}</td>
                <td className="p-2 text-gray-700">{slip.hours}</td>
                <td className="p-2 text-gray-700">{empRateMap[slip.employee] ? `$${empRateMap[slip.employee]}/hr` : ''}</td>
                <td className="p-2 text-gray-700">{formatMoney(slip.vehicle)}</td>
                <td className="p-2 text-gray-700">{formatMoney(slip.fuel_expense)}</td>
                <td className="p-2">
                  <div className="text-gray-700">{slip.customer || slip.project}</div>
                  {slip.project_address && <div className="text-gray-400 text-[10px]">{slip.project_address}</div>}
                </td>
                <td className="p-2 flex gap-1">
                  <button onClick={() => onEditSlip(slip)} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1" data-testid={`edit-timeslip-${index}`}>
                    <Edit className="w-3 h-3" />
                  </button>
                  {isAdmin && (
                    <button onClick={() => onDeleteSlip(slip)} className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1" data-testid={`delete-timeslip-${index}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreviousTimeslips;
